/**
 * Google Calendar / Outlook CSV import — the format people fall back to when a
 * calendar refuses an `.ics`, and the only one that bulk-imports from a spreadsheet.
 */

import { type EventDraft, effectiveEnd, timeClock, timeDate } from './model';
import { LIMITS, sanitizeLine, sanitizeText } from './sanitize';

export const CSV_HEADER = [
  'Subject',
  'Start Date',
  'Start Time',
  'End Date',
  'End Time',
  'All Day Event',
  'Description',
  'Location',
] as const;

/** `MM/DD/YYYY`, the only date form Google's importer accepts unambiguously. */
function csvDate(date: string): string {
  const [year = '', month = '', day = ''] = date.split('-');
  return `${month}/${day}/${year}`;
}

/** `09:00 AM` — the importer's 12-hour clock. */
function csvTime(clock: string | null): string {
  if (clock === null) return '';
  const [rawHours = '0', minutes = '00'] = clock.split(':');
  const hours = Number(rawHours);
  const suffix = hours < 12 ? 'AM' : 'PM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${String(hour12).padStart(2, '0')}:${minutes} ${suffix}`;
}

/**
 * Quotes a cell — and defuses spreadsheet formula injection first.
 *
 * A title like `=HYPERLINK("http://evil","click")` is inert in an .ics and *executable* in
 * Excel, Sheets, and LibreOffice when the CSV is opened. Prefixing a leading formula trigger
 * with an apostrophe makes the cell literal text; the apostrophe is not shown as content by
 * the importers. This is a property of the CSV format, so it belongs in the CSV writer.
 */
function csvCell(rawValue: string): string {
  const value = sanitizeText(rawValue, LIMITS.description);
  const defused = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\n]/.test(defused) ? `"${defused.replace(/"/g, '""')}"` : defused;
}

function csvRow(draft: EventDraft): string[] {
  const end = effectiveEnd(draft);
  return [
    sanitizeLine(draft.title, LIMITS.title),
    csvDate(timeDate(draft.start)),
    csvTime(timeClock(draft.start)),
    csvDate(timeDate(end)),
    csvTime(timeClock(end)),
    draft.start.kind === 'date' ? 'True' : 'False',
    sanitizeText(draft.description ?? '', LIMITS.description),
    sanitizeLine(draft.location ?? '', LIMITS.location),
  ];
}

export function toCsv(drafts: readonly EventDraft[]): string {
  return [[...CSV_HEADER], ...drafts.slice(0, LIMITS.events).map(csvRow)]
    .map((row) => row.map(csvCell).join(','))
    .join('\n')
    .concat('\n');
}
